import React from 'react';
import { useGate } from 'statsig-react';
import { useSnapshot } from 'valtio';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { EmailNotificationConfirmationModal } from '@components/modals/EmailNotificationModal';
import { useAuth } from '@context/auth/base';
import { ModalType, useModal } from '@context/modal';
import { useToast } from '@context/toast';
import {
  GetUserNotificationPreferencesDocument,
  UpdateNotificationEmailDocument,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogError';
import {
  CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  FEATURE_GATES,
  REPORT_ISSUE,
} from '@utils/constants';
import {
  clearEmailNotificationsErrors as clearErrors,
  setAllFieldsTouched,
  setArtistUpdates,
  setError,
  setField,
  validateEmailNotifications as validate,
} from './helpers';
import { ERRORS } from './schema';
import { emailNotificationsState } from './store';

export const useEmailNotificationsForm = () => {
  const { fields, errors, touched, artistUpdates } = useSnapshot(emailNotificationsState, {
    sync: true,
  });

  return {
    errors,
    fields,
    touched,
    validate,
    clearErrors,
    setField,
    setError,
    setArtistUpdates,
    setAllFieldsTouched,
    artistUpdates,
  };
};

export function useEmailNotifications() {
  const { isAuthenticatedWithAPI } = useAuth();
  const { value: showNotificationsFeatureGate } = useGate(FEATURE_GATES.EMAIL_NOTIFICATIONS);
  const { closeCancelModal, openModal } = useModal();

  const logError = useLogError();
  const { data, isLoading } = useQuery(GetUserNotificationPreferencesDocument, {
    enabled: isAuthenticatedWithAPI && showNotificationsFeatureGate,
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  const status = data?.data.userNotificationPreferences?.emailVerificationStatus;
  const email = data?.data.userNotificationPreferences?.email ?? '';
  const receiveArtistEmails =
    data?.data.userNotificationPreferences?.receiveArtistUpdateEmails || false;

  /**
   * Update Email
   */
  const { mutateAsync, isLoading: updatingEmail } = useMutation(
    UpdateNotificationEmailDocument,
    {},
  );

  const { openToast, closeToast } = useToast();

  const updateEmail = useStableCallback(
    async ({
      email,
      onVerify,
      onClaimedError,
    }: {
      email: string | null;
      onVerify?: () => void;
      onClaimedError?: () => void;
    }) => {
      try {
        const { data: emailData } = await mutateAsync({ email });

        closeToast();

        const typename = emailData?.updateNotificationEmail.__typename;

        if (typename === 'MutationUpdateNotificationEmailSuccess') {
          if (!email) return false;
          onVerify?.();

          closeCancelModal();

          openModal(ModalType.NOTIFICATIONS_EMAIL_CONFIRMATION, {
            body: <EmailNotificationConfirmationModal email={email} />,
          });

          return true;
        }

        if (typename === 'NotificationEmailAlreadyClaimedError') {
          if (!!onClaimedError) {
            onClaimedError();
          } else {
            openToast({
              text: ERRORS.email.non_unique,
              variant: 'error',
            });
          }

          return false;
        }

        if (typename === 'NotificationEmailFailedToSendError') {
          logError({
            error: new Error(emailData.updateNotificationEmail.message),
            toast: {
              text: (
                <>
                  We had an issue sending the verification email. Please let us know{' '}
                  <AccessibleLink
                    className="text-brand300 hover:text-brand600"
                    absoluteUri={REPORT_ISSUE}
                    type="external"
                  >
                    let us know
                  </AccessibleLink>{' '}
                  or try again
                </>
              ),
              variant: 'error',
            },
          });
          return false;
        }

        return false;
      } catch (error) {
        logError({
          error,
          toast: 'Something went wrong. Please try again.',
        });
        return false;
      }
    },
  );

  return { loading: isLoading, status, email, receiveArtistEmails, updateEmail, updatingEmail };
}
